import { IDateRange } from '../../../../../../../api/apiTypes';
import { isBefore, differenceInYears, isValid, isFuture } from 'date-fns';
import { useDateSelectorHasChanges } from './useDateSelectorHasChanges';

/**
 * Logic hook that determines a variety of flags for the date selector.
 */
export const useDateSelectorEditorLogic = ({
  startDate,
  endDate,
}: IDateRange): IDateSelectorEditorLogic => {
  const hasChanges = useDateSelectorHasChanges({ startDate, endDate });
  const isStartDateValid = startDate !== undefined && isValid(startDate);
  const isEndDateValid = endDate !== undefined && isValid(endDate);
  const areBothDatesValid = isStartDateValid && isEndDateValid;

  /** Is the end date before the start date in time? */
  const isEndBeforeStart = areBothDatesValid && isBefore(endDate, startDate);

  const isStartFuture = isStartDateValid && isFuture(startDate);
  const isEndFuture = isEndDateValid && isFuture(endDate);

  /** Is the user picking a date in the future */
  const isAnyDateAfterToday = isStartFuture || isEndFuture;

  /** Is the timespan longer than one year? */
  const isMoreThanOneYear =
    areBothDatesValid && differenceInYears(endDate, startDate) > 1.0;

  return {
    hasChanges,
    isEndBeforeStart,
    isAnyDateAfterToday,
    isMoreThanOneYear,
    isStartDateValid,
    isEndDateValid,
  };
};

export interface IDateSelectorEditorLogic {
  /** Does the user have unsaved changes? */
  hasChanges: boolean;
  /** Is the end date before the start date in time? */
  isEndBeforeStart: boolean;
  /** Is the user picking a date in the future */
  isAnyDateAfterToday: boolean;
  /** Is the timespan longer than one year? */
  isMoreThanOneYear: boolean;
  /** Is the start date valid? */
  isStartDateValid: boolean;
  /** Is the end date valid? */
  isEndDateValid: boolean;
}
